if (!customElements.get('card-slider')) {
  class CardSlider extends HTMLElement {
    connectedCallback() {
      this.refreshElements();

      if (!this.track) return;

      this.currentIndex = 0;
      this.pointerId = null;
      this.startX = 0;
      this.startY = 0;
      this.startScroll = 0;
      this.dragging = false;
      this.axisLocked = null;
      this.didDrag = false;
      this.snapTimeout = null;
      this.hoverInterval = null;
      this.isHovered = false;

      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onClick = this.onClick.bind(this);

      // Touch handlers for manual swipe
      this.addEventListener(
        'touchstart',
        (event) => {
          if (event.touches.length > 1) return;
          // Do NOT stop propagation unconditionally to allow vertical page scrolling
          const touch = event.touches[0];
          this.startX = touch.clientX;
          this.startY = touch.clientY;
          this.startScroll = this.track.scrollLeft;
          this.axisLocked = null;
          this.dragging = false;
          this.didDrag = false;
        },
        { passive: true }
      );

      this.addEventListener(
        'touchmove',
        (event) => {
          if (event.touches.length > 1) return;
          const touch = event.touches[0];
          const dx = touch.clientX - this.startX;
          const dy = touch.clientY - this.startY;

          if (!this.axisLocked) {
            if (Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
            this.axisLocked = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';
          }

          if (this.axisLocked === 'x') {
            event.preventDefault();
            event.stopPropagation();
            this.dragging = true;
            this.didDrag = true;
            this.track.style.scrollSnapType = 'none';
            this.track.scrollLeft = this.startScroll - dx;
          }
        },
        { passive: false }
      );

      this.addEventListener(
        'touchend',
        (event) => {
          if (this.axisLocked === 'x') {
            event.stopPropagation();
          }
          this.track.style.scrollSnapType = '';
          if (this.axisLocked === 'x' && this.dragging) {
            const dx = (event.changedTouches[0]?.clientX || this.startX) - this.startX;
            const width = this.slideWidth();
            const threshold = Math.max(30, width * 0.15);
            let next = this.currentIndex;

            if (Math.abs(dx) > threshold) {
              next = dx < 0 ? this.currentIndex + 1 : this.currentIndex - 1;
            }

            this.goTo(next, true);
          }
          this.axisLocked = null;
          this.dragging = false;
        },
        { passive: true }
      );

      // Mouse wheel / trackpad scroll
      let wheelTimeout = null;
      this.addEventListener(
        'wheel',
        (event) => {
          this.refreshElements();
          if (this.slides.length <= 1) return;
          
          // Only handle horizontal scrolling
          if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) {
            return; // Allow native vertical page scrolling
          }

          const delta = event.deltaX;
          if (Math.abs(delta) < 4) return;

          event.preventDefault();
          event.stopPropagation();

          if (wheelTimeout) return;
          wheelTimeout = setTimeout(() => {
            wheelTimeout = null;
          }, 180);

          const direction = delta > 0 ? 1 : -1;
          const targetIndex = Math.max(0, Math.min(this.currentIndex + direction, this.slides.length - 1));
          if (targetIndex !== this.currentIndex) {
            this.goTo(targetIndex, true);
          }
        },
        { passive: false }
      );

      this.track.addEventListener('pointerdown', this.onPointerDown);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('click', this.onClick);
      this.track.addEventListener(
        'dragstart',
        (event) => {
          event.preventDefault();
        },
        { passive: false }
      );

      this.bindDots();
      this.setActiveDot(0);
    }

    refreshElements() {
      this.track = this.querySelector('.card-slider__track');
      this.dots = Array.from(this.querySelectorAll('.card-slider__dot'));
      this.slides = Array.from(this.querySelectorAll('.card-slider__slide'));
    }

    bindDots() {
      this.refreshElements();
      this.dots.forEach((dot, index) => {
        const handleDotClick = (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (event.stopImmediatePropagation) event.stopImmediatePropagation();
          this.goTo(index, true);
        };
        dot.addEventListener('click', handleDotClick);
        dot.addEventListener('pointerdown', (e) => e.stopPropagation());
        dot.addEventListener('mousedown', (e) => e.stopPropagation());
        dot.addEventListener('touchstart', (e) => e.stopPropagation(), { passive: true });
      });
    }

    onMouseEnter() {
      this.refreshElements();
      if (this.slides.length <= 1 || this.isHovered) return;
      this.isHovered = true;
      this.startAutoSlide(true);
    }

    onMouseLeave() {
      if (!this.isHovered) return;
      this.isHovered = false;
      this.stopAutoSlide();
      this.goTo(0, true);
    }

    startAutoSlide(instant = false) {
      this.refreshElements();
      if (this.slides.length <= 1) return;
      this.stopAutoSlide();

      if (instant) {
        const nextIndex = (this.currentIndex + 1) % this.slides.length;
        this.goTo(nextIndex, true);
      }

      this.hoverInterval = setInterval(() => {
        if (!this.isHovered) {
          this.stopAutoSlide();
          return;
        }
        this.refreshElements();
        if (this.slides.length <= 1) return;
        const nextIndex = (this.currentIndex + 1) % this.slides.length;
        this.goTo(nextIndex, true);
      }, 1000);
    }

    stopAutoSlide() {
      if (this.hoverInterval) {
        clearInterval(this.hoverInterval);
        this.hoverInterval = null;
      }
    }

    disconnectedCallback() {
      this.stopAutoSlide();
      this.unbindPointer();
    }

    slideWidth() {
      this.refreshElements();
      const firstSlide = this.slides[0];
      if (firstSlide) {
        const rect = firstSlide.getBoundingClientRect();
        if (rect && rect.width > 0) {
          return rect.width;
        }
      }
      if (this.track && this.track.clientWidth > 0) {
        return this.track.clientWidth;
      }
      return this.clientWidth || 300;
    }

    onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.target.closest('.card-slider__dot')) return;

      this.pointerId = event.pointerId;
      this.startX = event.clientX;
      this.startY = event.clientY;
      this.startScroll = this.track.scrollLeft;
      this.dragging = false;
      this.axisLocked = null;
      this.didDrag = false;
      this.track.style.scrollBehavior = 'auto';

      this.track.addEventListener('pointermove', this.onPointerMove);
      this.track.addEventListener('pointerup', this.onPointerUp);
      this.track.addEventListener('pointercancel', this.onPointerUp);
    }

    onPointerMove(event) {
      if (event.pointerId !== this.pointerId) return;

      const dx = event.clientX - this.startX;
      const dy = event.clientY - this.startY;

      if (!this.axisLocked) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        this.axisLocked = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';

        // Only capture pointer and stop propagation if horizontal swipe is detected
        if (this.axisLocked === 'x') {
          try {
            this.track.setPointerCapture(event.pointerId);
          } catch (error) {}
        }
      }

      if (this.axisLocked !== 'x') return;

      this.dragging = true;
      this.didDrag = true;
      event.preventDefault();
      event.stopPropagation();
      this.track.scrollLeft = this.startScroll - dx;
    }

    onPointerUp(event) {
      if (event.pointerId !== this.pointerId) return;

      const dx = event.clientX - this.startX;
      
      // Release pointer capture if horizontal
      if (this.axisLocked === 'x') {
        try {
          this.track.releasePointerCapture(event.pointerId);
        } catch (error) {}
      }

      this.unbindPointer();
      this.track.style.scrollBehavior = '';

      if (this.axisLocked === 'x' && this.dragging) {
        event.preventDefault();
        event.stopPropagation();
        const width = this.slideWidth();
        const threshold = Math.max(40, width * 0.18);
        let next = Math.round(this.track.scrollLeft / width);

        if (Math.abs(dx) > threshold) {
          next = dx < 0 ? this.currentIndex + 1 : this.currentIndex - 1;
        }

        this.goTo(next, true);
      }
    }

    unbindPointer() {
      if (!this.track) return;
      this.track.removeEventListener('pointermove', this.onPointerMove);
      this.track.removeEventListener('pointerup', this.onPointerUp);
      this.track.removeEventListener('pointercancel', this.onPointerUp);
      this.pointerId = null;
      this.dragging = false;
      this.axisLocked = null;
    }

    onScroll() {
      if (!this.track || !this.slides.length) return;
      const scrollLeft = this.track.scrollLeft;
      const width = this.track.clientWidth || 1;
      const closestIndex = Math.round(scrollLeft / width);
      if (closestIndex >= 0 && closestIndex < this.slides.length && closestIndex !== this.currentIndex) {
        this.setActiveDot(closestIndex);
      }
    }

    onClick(event) {
      if (event.target.closest('.card-slider__dot')) return;

      if (this.didDrag) {
        event.preventDefault();
        event.stopPropagation();
        this.didDrag = false;
        return;
      }

      const url =
        this.dataset.productUrl ||
        this.closest('.card-wrapper')?.querySelector('a.full-unstyled-link')?.href;
      if (!url) return;

      event.preventDefault();
      event.stopPropagation();
      window.location.href = url;
    }

    goTo(index, smooth = false) {
      this.refreshElements();
      if (!this.track || !this.slides.length) return;

      const max = this.slides.length - 1;
      const next = Math.max(0, Math.min(index, max));
      this.currentIndex = next;
      this.setActiveDot(next);

      const width = this.slideWidth();
      const targetLeft = next * width;

      this.track.style.scrollSnapType = 'none';

      this.track.scrollTo({
        left: targetLeft,
        behavior: smooth ? 'smooth' : 'auto',
      });

      if (this.snapTimeout) clearTimeout(this.snapTimeout);
      this.snapTimeout = setTimeout(() => {
        if (this.track) {
          this.track.style.scrollSnapType = '';
        }
      }, smooth ? 350 : 50);
    }

    setActiveDot(index) {
      this.currentIndex = index;
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
      });
    }
  }

  customElements.define('card-slider', CardSlider);

  // Global Delegated Event Listeners for Bulletproof Hover Auto-Sliding
  document.addEventListener(
    'mouseover',
    (e) => {
      const cardSlider =
        e.target.closest('card-slider') ||
        e.target.closest('.card-wrapper')?.querySelector('card-slider') ||
        e.target.closest('.card')?.querySelector('card-slider') ||
        e.target.closest('.grid__item')?.querySelector('card-slider') ||
        e.target.closest('li')?.querySelector('card-slider');
      if (cardSlider && typeof cardSlider.onMouseEnter === 'function') {
        cardSlider.onMouseEnter();
      }
    },
    { passive: true }
  );

  document.addEventListener(
    'mouseout',
    (e) => {
      const cardSlider =
        e.target.closest('card-slider') ||
        e.target.closest('.card-wrapper')?.querySelector('card-slider') ||
        e.target.closest('.card')?.querySelector('card-slider') ||
        e.target.closest('.grid__item')?.querySelector('card-slider') ||
        e.target.closest('li')?.querySelector('card-slider');
      if (cardSlider) {
        const related = e.relatedTarget;
        if (
          related &&
          (cardSlider.contains(related) ||
            cardSlider.closest('.card-wrapper')?.contains(related) ||
            cardSlider.closest('.card')?.contains(related) ||
            cardSlider.closest('.grid__item')?.contains(related) ||
            cardSlider.closest('li')?.contains(related))
        )
          return;
        if (typeof cardSlider.onMouseLeave === 'function') {
          cardSlider.onMouseLeave();
        }
      }
    },
    { passive: true }
  );

  // Intercept Dot Clicks in Capture Phase to Prevent Dawn Card Stretched Link Interception
  document.addEventListener(
    'click',
    (e) => {
      const dot = e.target.closest('.card-slider__dot');
      if (!dot) return;

      const cardSlider =
        dot.closest('card-slider') ||
        dot.closest('.card-wrapper')?.querySelector('card-slider') ||
        dot.closest('.card')?.querySelector('card-slider');
      if (!cardSlider) return;

      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();

      const dots = Array.from(cardSlider.querySelectorAll('.card-slider__dot'));
      const index = dots.indexOf(dot);
      if (index !== -1 && typeof cardSlider.goTo === 'function') {
        cardSlider.goTo(index, true);
      }
    },
    true
  );
}
